import pipe from "lodash/fp/pipe";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  BasePoint,
  createEditor,
  Descendant,
  Editor,
  Range,
  Node as SlateNode,
  Text as SlateText,
  Transforms
} from "slate";

import { withHistory } from "slate-history";
import { Editable, ReactEditor, Slate, withReact } from "slate-react";

import isHotkey from "is-hotkey";

import { ProviderConsumer as FluentUIThemeConsumer } from "@fluentui/react-northstar";
import { Person, PersonViewType } from "@microsoft/mgt-react";
import { useTranslation } from "react-i18next";
import "./Editor.css";
import {
  deepEqual,
  ensureParagraphOnEmpty,
  extractReplacementText,
  HOTKEYS,
  COMMENT_FRAGMENT_TYPE,
  deleteFromKeyboard,
  htmlParser,
  insertMention,
  isAutocorrectMisreportedAsBreak,
  isKeyboardDeletion,
  replaceOnCompositionEnd,
  sanitizeAutocorrectedText,
  toggleMark,
  withBullets,
  withHtml,
  withMentions,
  withReopenedCompositions,
} from "./slate-editor-helpers";
import SlateToolbar from "./SlateComponents/SlateToolbar";
import { El, Leaf } from "./SlateEl";
import { Portal } from "./SlatePortal";
import { Toast } from "../toast/Toast";

interface SlateEditorProps {
  onChange: (data: any, editor?:any) => void;
  slateValue?: Descendant[];
  markdownString?: string;
  isFocus: boolean;
  noImage: boolean;
  usersInMention?: PlatformUser[];
  readOnly?: boolean;
  onImagePaste?: (data: File) => void;
  minHeight?: string;
  /** Drops the fixed height so the box grows with its text between minHeight and this. */
  maxHeight?: string;
  className?: string;
  disabled?: boolean;
  onFocus?: () => void;
  noHeaders?: boolean;
  helpcardProps?: any;
  scopedButtons?: any[];
  editor?:any;
  customToolbar?:any[];
  toolbarEnd?: React.ReactNode;
  onBlur?:() => void;
  onKeyDown?:(event:any, editor:any) => void;
  verticalResize?:boolean;
}

const SlateEditor: React.FC<SlateEditorProps> = (props: SlateEditorProps) => {
  const { t } = useTranslation();
  const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  let theme = localStorage.getItem("TikitTheme") ?? "light";

  if (theme === "system") {
    theme = isDark ? "dark" : "light";
  }

  const imageSrcToFile = async (src: string): Promise<File | null> => {
    try {
      const response = await fetch(src);
      if (!response.ok) return null;
      const blob = await response.blob();
      // A 200 isn't proof of an image: auth-walled sources answer with a
      // sign-in page, which would otherwise be attached under a .png name.
      if (!blob.type.startsWith("image/") || blob.size === 0) return null;
      const [, extension] = blob.type.split("/");
      return new File([blob], `pasted-image.${extension || "png"}`, { type: blob.type });
    } catch {
      // Cross-origin images without CORS headers can't be fetched from the
      // client - drop them rather than leaving a broken inline <img>.
      return null;
    }
  };

  const withImages = (editor) => {
    const { insertData, isVoid } = editor;

    editor.isVoid = (element) => {
      return element.type === "image" ? true : isVoid(element);
    };

    editor.insertData = (data) => {
      const { files } = data;

      if (files && files.length > 0) {
        const file = files[0];
        const [mime] = file.type.split("/");

        if (mime === "image" && props.onImagePaste) props.onImagePaste(file);
        return;
      }

      const html = data.getData("text/html");
      const body = html ? new DOMParser().parseFromString(html, "text/html").body : null;
      const images = body ? Array.from(body.querySelectorAll("img")) : [];

      if (images.length > 0 && props.onImagePaste) {
        const sources = images
          .map((img) => {
            const src = img.getAttribute("src");
            img.remove();
            return src;
          })
          .filter(Boolean);

        Promise.all(sources.map(imageSrcToFile)).then((files) => {
          files.forEach((file) => file && props.onImagePaste(file));
          if (files.some((file) => !file)) {
            Toast.warn(t("ticket-details.ticket-conversation.pasted-image-unavailable"));
          }
        });

        // Pasted text + image: keep the text, route the image(s) to the
        // attachment list above instead of embedding them in the comment.
        // An image-only paste leaves no text behind, so there's nothing left
        // to insert into the document.
        if (body.textContent?.trim()) {
          insertData({ getData: (type: string) => (type === "text/html" ? body.innerHTML : data.getData(type)) });
        }
        return;
      }

      insertData(data);
    };

    return editor;
  };

  const renderElement = useCallback(
    (elProps) => <El {...elProps} theme={theme} scopedButtons={props.scopedButtons} />,
    [props.scopedButtons]
  );
  const renderLeaf = useCallback(
    (leafProps) => <Leaf {...leafProps} theme={theme} scopedButtons={props.scopedButtons} />,
    [props.scopedButtons]
  );
  const mentionListRef = useRef<HTMLDivElement | null>();

const withInlines = (editor) => {
  const inlineElementTypes = ["scoped-text", "remove-scoped-text","mention"];
  const { isInline } = editor;
  editor.isInline = (element) =>
    inlineElementTypes.includes(element.type) || isInline(element);
  return editor;
};

  const createEditorWithPlugins = pipe(
    withReact,
    withHistory,
    withHtml,
    withBullets,
    withImages,
    withMentions,
    withInlines,
    withReopenedCompositions
  );

  const editor = useMemo(() => createEditorWithPlugins(createEditor()), []);

  const [mentionTarget, setMentionTarget] = useState<Range | undefined>();
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionSearch, setMentionSearch] = useState(null);
  const [mentionUsers, setMentionUsers] = useState<PlatformUser[]>([]);
  const onChangeFiredRef = useRef(false);
  const justAutocorrectedRef = useRef(false);
  // What the open composition took up as it began; undefined until its first insertCompositionText.
  const compositionRangeRef = useRef<Range | null | undefined>(undefined);

  if (props.editor)
    props.editor(editor);

  useEffect(() => {
    if (props.usersInMention && mentionSearch !== null) {
      if (mentionSearch === "") {
        setMentionUsers(props.usersInMention);
      }
      const peopleToMention: PlatformUser[] = props.usersInMention.filter(
        (user) =>
          user.FullName?.toLowerCase().startsWith(mentionSearch.toLowerCase()) ||
          user.FamilyName?.toLowerCase().startsWith(mentionSearch.toLowerCase())
      );
      if (peopleToMention.length === 0) {
        setMentionTarget(null);
        setMentionIndex(0);
      } else {
        setMentionUsers(peopleToMention);
      }
    }
  }, [mentionSearch]);

  const getPlainText = (nodes) => {
    return nodes.map((n) => SlateNode.string(n)).join("\n");
  };

  const [isFirstMention, setIsFirstMention] = useState<boolean>(false);

  const hasMatchingUser = (text: string) =>
    props.usersInMention.some(
      (user) =>
        user.FullName?.toLowerCase().startsWith(text.toLowerCase()) ||
        user.FamilyName?.toLowerCase().startsWith(text.toLowerCase())
    );

  const handleChange = (nextValue: Descendant[]) => {
    setValue(nextValue);
    const { selection } = editor;

    if (selection && Range.isCollapsed(selection) && value !== nextValue) {
      const [start] = Range.edges(selection);

      const characterBefore = Editor.before(editor, start, {
        unit: "character",
      });
      const twoCharsBefore = characterBefore && Editor.before(editor, characterBefore);
      const twoCharsBeforeRange = twoCharsBefore && Editor.range(editor, twoCharsBefore, start);
      const twoCharsBeforeText = twoCharsBeforeRange && Editor.string(editor, twoCharsBeforeRange);

      let wordBefore = Editor.before(editor, start, { unit: "word" });
      let before = wordBefore && Editor.before(editor, wordBefore);
      let beforeRange = before && Editor.range(editor, before, start);
      let beforeText = beforeRange && Editor.string(editor, beforeRange);

      if (before) {
        while ((before?.offset ?? 0) > 0 && !beforeText.startsWith("@")) {
          const [startForWordBefore] = Range.edges({
            anchor: {
              offset: before.offset - 1,
              path: before.path,
            },
            focus: {
              offset: before.offset - 1,
              path: before.path,
            },
          });
          wordBefore = Editor.before(editor, startForWordBefore, {
            unit: "word",
          });
          before = wordBefore && Editor.before(editor, wordBefore);

          beforeRange = before
            ? Editor.range(editor, before, start)
            : beforeRange;
          beforeText = beforeRange && Editor.string(editor, beforeRange);
        }
      }

      const beforeMatch = beforeText && /^@([\w\s]+)$/.exec(beforeText);

      const after = Editor.after(editor, start);
      const afterRange = Editor.range(editor, start, after);
      const afterText = Editor.string(editor, afterRange);
      const afterMatch = /^(\s|$)/.exec(afterText);

      const afterFirstCharacter: BasePoint = {
        path: [0, 0],
        offset: 1,
      };

      if (beforeMatch && afterMatch && hasMatchingUser(beforeMatch[1])) {
        setMentionTarget(beforeRange);
        setMentionSearch(beforeMatch[1]);
        setMentionIndex(0);
        return;
      } else if (
        twoCharsBeforeText &&
        twoCharsBeforeText?.slice(1).startsWith("@") &&
        twoCharsBeforeText.startsWith(" ")
      ) {
        setMentionTarget(twoCharsBeforeRange);
        setMentionSearch("");
        setMentionIndex(0);
        return;
      } else if (
        getPlainText(nextValue).length > 0 &&
        getPlainText(nextValue).startsWith("@") &&
        deepEqual(start, afterFirstCharacter)
      ) {
        setIsFirstMention(true);
        setMentionTarget(Editor.range(editor, afterFirstCharacter));
        setMentionSearch("");
        setMentionIndex(0);
        return;
      }
    }
    setMentionTarget(null);
    if (value !== nextValue || !onChangeFiredRef.current) {
      onChangeFiredRef.current = true;
      props.onChange(nextValue, editor);
    }
  };

  const onKeyDown = useCallback(
    (event, editor) => {
      if (props.onKeyDown)
        props.onKeyDown(event,editor);

      // stop a parent Chat FocusZone (when editing a comment) from moving focus to another message
      if (
        event.key === "ArrowUp" ||
        event.key === "ArrowDown" ||
        event.key === "ArrowLeft" ||
        event.key === "ArrowRight" ||
        event.key === "Home" ||
        event.key === "End"
      ) {
        event.stopPropagation();
      }

      if (event.getModifierState("Control") || event.getModifierState("Meta")) {
        if (event.key === "z") {
          event.preventDefault();
          const canUndo = editor.history?.undos.length > 0;
          if (canUndo) {
            editor.undo(editor);
          }
        }
      }
      if (
        event.key === "Backspace" &&
        Editor.string(editor, []).trim() === ""
      ) {
        ensureParagraphOnEmpty(editor);
      }
      if (editor.isComposing) {
        return;
      }

      for (const hotkey in HOTKEYS) {
        if (isHotkey(hotkey, event)) {
          event.preventDefault();
          const mark = HOTKEYS[hotkey];
          toggleMark(editor, mark);
        }
      }

      if (mentionTarget && mentionUsers.length > 0) {
        switch (event.key) {
          case "ArrowDown":
            event.preventDefault();
            const nextIndex = mentionIndex >= mentionUsers.length - 1 ? 0 : mentionIndex + 1;
            setMentionIndex(nextIndex);
            break;
          case "ArrowUp":
            event.preventDefault();
            const prevIndex = mentionIndex <= 0 ? mentionUsers.length - 1 : mentionIndex - 1;
            setMentionIndex(prevIndex);
            break;
          case "Tab":
          case "Enter":
            event.preventDefault();
            Transforms.select(editor, mentionTarget);
            insertMention(
              editor,
              mentionUsers[mentionIndex].FullName,
              mentionUsers[mentionIndex].AadObjectId ?? mentionUsers[mentionIndex].UserName,
              isFirstMention
            );
            setIsFirstMention(false);
            setMentionTarget(null);
            break;
          case "Escape":
            event.preventDefault();
            setMentionTarget(null);
            break;
        }
      }
    },
    [mentionUsers, editor, mentionIndex, mentionTarget]
  );

  useEffect(() => {
    if (mentionTarget && mentionUsers.length > 0) {
      const el = mentionListRef.current;
      const domRange = ReactEditor.toDOMRange(editor, mentionTarget);
      const rect = domRange.getBoundingClientRect();
      el.style.bottom = `calc(100% - ${rect.top + window.scrollY}px)`;
      el.style.left = `${rect.left + window.scrollX}px`;
    }
  }, [mentionUsers.length, editor, mentionIndex, mentionSearch, mentionTarget]);

  const [value, setValue] = useState<Descendant[]>(props.slateValue);
  const [hasMounted, setHasMounted] = useState(false);
  const [initialScrollDone, setInitialScrollDone] = useState(false);

  useEffect(() => {
    if (!initialScrollDone)
    {  
      requestAnimationFrame(() => {
        const editorEl = document.querySelector('.slate-editor-editable');
        if (editorEl) {
          editorEl.scrollTop = 0;
          setInitialScrollDone(true);
        }
      });
    }
  }, [value]);

  useEffect(() => {
    setHasMounted(true);
    return () => {
      setMentionTarget(null);
      setMentionSearch(null);
    };
  }, []);


  useEffect(() => {
    if (props.isFocus && hasMounted) {
      setTimeout(() => {
        const textEntries = Array.from(
          Editor.nodes(editor, {
            at: [],
            match: (n) => SlateText.isText(n),
            voids: false,
          })
        );

        if (textEntries.length > 0) {
          const [lastText, lastPath] = textEntries[textEntries.length - 1];
          Transforms.select(editor, {
            path: lastPath,
            offset: (lastText as any).text.length,
          });
        } else {
          // content is only void blocks (e.g. an image), so there is nowhere to put the caret
          Transforms.insertNodes(
            editor,
            { type: "paragraph", children: [{ text: "" }] } as any,
            { at: [editor.children.length] }
          );
          Transforms.select(editor, Editor.end(editor, [editor.children.length - 1]));
        }

        ReactEditor.focus(editor);
      }, 0);
    }
  }, [hasMounted, editor, props.isFocus]);

  const findDifference = (original, updated) => {
    let start = 0;
    while (start < original.length && original[start] === updated[start]) {
      start++;
    }

    let endOriginal = original.length - 1;
    let endUpdated = updated.length - 1;
    while (
      endOriginal >= start &&
      endUpdated >= start &&
      original[endOriginal] === updated[endUpdated]
    ) {
      endOriginal--;
      endUpdated--;
    }

    return {
      start,
      removed: original.slice(start, endOriginal + 1),
      added: updated.slice(start, endUpdated + 1),
    };
  };

  const getForegroundColor = (
    { siteVariables: { colorScheme } }: any,
    disabled?: boolean
  ) => {
    return disabled
      ? colorScheme.default.foregroundDisabled1
      : colorScheme.default.foreground;
  };

  // Syncs native DOM edits that Slate never intercepted back into the model
  const handleOnInput = () => {
    if (editor.isComposing) {
      return;
    }
    const domSelection = window.getSelection();
    if (!domSelection || !editor.selection) return;

    const domNode = domSelection.anchorNode;

    if (domNode.textContent) {
      // Slate fills an empty line with a zero-width character, which is not text anyone typed.
      const domText = domNode.textContent.replace(/\uFEFF/g, "");
      const leafPath = editor.selection.anchor.path;
      const [leaf] = Editor.node(editor, leafPath);
      const leafText = SlateNode.string(leaf);

      const { start, removed, added } = findDifference(leafText, domText);
      if (!removed && !added) return;

      const range = {
        anchor: { path: leafPath, offset: start },
        focus: { path: leafPath, offset: start + removed.length },
      };

      Transforms.insertText(editor, sanitizeAutocorrectedText(added), {
        at: range,
      });
    }
  };

  const handleDOMBeforeInput = useCallback(
    (event: InputEvent) => {
      // guard clause - on Safari, Slate also calls this for an event the capture listener already handled
      if (editor.isComposing || event.defaultPrevented) return;

      if (event.inputType === "insertReplacementText") {
        event.preventDefault();
        const [targetRange] = event.getTargetRanges?.() ?? [];
        if (targetRange) {
          const slateRange = ReactEditor.toSlateRange(editor, targetRange);
          if (slateRange) {
            Transforms.select(editor, slateRange);
          }
        }
        const replacement = extractReplacementText(event);
        if (replacement) {
          Transforms.insertText(editor, sanitizeAutocorrectedText(replacement));
        } else if (targetRange) {
          // no replacement text, so don't leave the word selected for the next keystroke to overwrite
          Transforms.collapse(editor, { edge: "end" });
        }
        justAutocorrectedRef.current = true;
        return;
      }

      const wasJustAutocorrected = justAutocorrectedRef.current;
      justAutocorrectedRef.current = false;

      if (event.inputType === "insertParagraph" || event.inputType === "insertLineBreak") {
        const [targetRange] = event.getTargetRanges?.() ?? [];

        if (
          isAutocorrectMisreportedAsBreak(
            event.inputType,
            targetRange,
            wasJustAutocorrected
          )
        ) {
          // iOS sent the space that confirms an autocorrect as a line break
          event.preventDefault();
          if (targetRange && targetRange.collapsed === false) {
            const slateRange = ReactEditor.toSlateRange(editor, targetRange);
            if (slateRange) {
              Transforms.select(editor, slateRange);
            }
            const replacement = extractReplacementText(event);
            if (replacement) {
              Transforms.insertText(
                editor,
                sanitizeAutocorrectedText(replacement)
              );
            } else {
              // otherwise the space below would replace the selected word
              Transforms.collapse(editor, { edge: "end" });
            }
          }
          Transforms.insertText(editor, " ");
        }
      }
    },
    [editor]
  );

  useEffect(() => {
    const editable = ReactEditor.toDOMNode(editor, editor);
    const targetSlateRange = (event: InputEvent): Range | null => {
      const [targetRange] = event.getTargetRanges?.() ?? [];
      try {
        return targetRange ? ReactEditor.toSlateRange(editor, targetRange) : null;
      } catch {
        return null;
      }
    };
    const onBeforeInputCapture = (event: InputEvent) => {
      if (event.inputType === "insertReplacementText") handleDOMBeforeInput(event);

      // Read before the composition changes the page, while the page and the editor still agree.
      if (event.inputType === "insertCompositionText" && compositionRangeRef.current === undefined) {
        compositionRangeRef.current = targetSlateRange(event);
      }
    };
    // Not in the capture phase, so that it comes after Slate's own listener, which takes deletions on
    // Safari and iOS.
    const onBeforeInput = (event: InputEvent) => {
      if (!isKeyboardDeletion(event.inputType) || event.defaultPrevented || !event.cancelable || editor.isComposing) return;
      event.preventDefault();
      deleteFromKeyboard(editor, event.inputType, targetSlateRange(event));
    };
    editable.addEventListener("beforeinput", onBeforeInputCapture, true);
    editable.addEventListener("beforeinput", onBeforeInput);
    return () => {
      editable.removeEventListener("beforeinput", onBeforeInputCapture, true);
      editable.removeEventListener("beforeinput", onBeforeInput);
    };
  }, [editor, handleDOMBeforeInput]);

  const onCopy = (event) => {
    const { selection } = editor;

    if (!selection) {
      return;
    }

    const fragment = Editor.fragment(editor, selection);
    const fragmentString = Editor.string(editor, selection);
    event.clipboardData.setData("text/html", htmlParser(fragment));
    event.clipboardData.setData("text/plain", fragmentString);
    // Read back by withHtml's paste, so lines copied within the editor paste as they were.
    event.clipboardData.setData(COMMENT_FRAGMENT_TYPE, JSON.stringify(fragment));
    event.preventDefault();
  };

  // Cut is copy, then the selection goes. Left to Slate's default it wrote a fragment the paste
  // does not read, so cut-and-paste within the editor gained the same empty lines copy did.
  const onCut = (event) => {
    if (!editor.selection || Range.isCollapsed(editor.selection)) return;
    onCopy(event);
    Editor.deleteFragment(editor);
  };

  const toolbar = (
    <SlateToolbar
      editor={editor}
      noImage={props.noImage}
      value={value}
      noHeaders={props.noHeaders}
      helpcardProps={props.helpcardProps}
      customToolbar={props.customToolbar}
    />
  );

  return (
    <FluentUIThemeConsumer
      render={(globalTheme) => (
        <div style={{ position: "relative" }} className={props.className}>
          
          <Slate editor={editor} value={value} onChange={handleChange}>
            {props.toolbarEnd ? (
              // Beside the toolbar rather than in it: the toolbar is scaled down on small
              // screens, which would pull these controls in from the edge.
              <div className="flex items-center">
                <div className="flex-1 min-w-0">{toolbar}</div>
                <div className="flex items-center flex-shrink-0">{props.toolbarEnd}</div>
              </div>
            ) : toolbar}

            
            <Editable
              onBlur={props.onBlur}
              renderElement={renderElement}
              renderLeaf={renderLeaf}
              onKeyDown={(event) => onKeyDown(event, editor)}
              spellCheck="true"
              readOnly={(props.readOnly || props.disabled) ?? false}
              onFocusCapture={(event) => {
                event.stopPropagation();
              }}
              onInput={handleOnInput}
              onDOMBeforeInput={handleDOMBeforeInput}
              onCompositionStart={(_event) => {
                editor.isComposing = true;
                compositionRangeRef.current = undefined;
              }}
              onCompositionEnd={(event) => {
                editor.isComposing = false;
                replaceOnCompositionEnd(editor, compositionRangeRef.current, event.data);
                compositionRangeRef.current = undefined;
              }}
              onCopy={onCopy}
              onCut={onCut}
              className={`slate-editor-editable p-3 border-none focus:border-b-2 focus:border-b-teams rounded outline-none ${props.maxHeight ? '' : 'h-24'} overflow-auto`}
              style={{
                ...(props.verticalResize ? {resize: 'vertical'} : {}),
                ...(props.minHeight ? { minHeight: props.minHeight } : {}),
                ...(props.maxHeight ? { maxHeight: props.maxHeight } : {}),
                color: getForegroundColor(globalTheme, props.disabled),
              }}
              {...{ spellcheck: "true" }}
            />
            {mentionTarget && mentionUsers.length > 0 && (
              <Portal>
                <div
                  ref={mentionListRef}
                  style={{
                    bottom: "-9999px",
                    left: "-9999px",
                    position: "absolute",
                    zIndex: 9999999,
                    padding: "3px",
                    background:
                      globalTheme.siteVariables.colorScheme.default.background,
                    borderRadius: "4px",
                    boxShadow: "0 1px 5px rgba(0,0,0,.2)",
                    maxHeight: "35vh",
                    overflow: "auto",
                  }}
                  className="mentions-container drop-shadow-lg"
                  data-cy="mentions-portal"
                >
                  <div
                    className="px-2 py-3"
                    style={{
                      color: `${globalTheme.siteVariables.colorScheme.default.foreground}`,
                    }}
                  >
                    {t("ticket-details.ticket-conversation.suggestions")}
                  </div>
                  {mentionUsers.map((user, i) => (
                    <div
                      key={user.Email}
                      style={{ padding: "1px 3px", borderRadius: "3px" }}
                      className={`${
                        i === mentionIndex ? "bg-purple-400 text-white" : ""
                      } mention-selection cursor-pointer`}
                    >
                      <a
                        className={"block p-2 mention-selection"}
                        href="/"
                        onClick={(e) => {
                          e.preventDefault();
                          Transforms.select(editor, mentionTarget);
                          insertMention(
                            editor,
                            user.FullName,
                            user.AadObjectId ?? user.UserName,
                            isFirstMention
                          );
                          setIsFirstMention(false);
                          setMentionTarget(null);
                          ReactEditor.focus(editor);
                        }}
                      >
                        <div className="mention-selection flex items-center">
                          <Person
                            personQuery={user.Email}
                            view={PersonViewType.avatar}
                            avatarSize="small"
                          />
                          <div
                            className="ml-2 mention-selection"
                            style={{
                              color: `${globalTheme.siteVariables.colorScheme.default.foreground}`,
                            }}
                          >
                            <div className="mention-selection">
                              {user.FullName}
                            </div>
                            <div className="mention-selection text-xs">
                              {user.Email}
                            </div>
                          </div>
                        </div>
                      </a>
                    </div>
                  ))}
                </div>
              </Portal>
            )}
          </Slate>
        </div>
      )}
    />
  );
};

export default SlateEditor;
